import { Fragment } from 'react';
import { Camera, Check, Mic, Monitor, Plus, ScrollText, Trash2, Waves } from 'lucide-react';
import { captureLabel, resolveSegmentConfig } from '@/lib/pitch/pitchSegmentConfig';

const CAPTURE_ICONS = { audio: Mic, camera: Camera, screen: Monitor };

function InsertButton({ position, label, onInsert, disabled }) {
  return (
    <button
      type="button"
      onClick={() => onInsert(position)}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={`pitch-insert-module-${position}`}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-dashed border-slate-600 text-slate-400 transition-colors hover:border-amber-400 hover:text-amber-300 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <Plus className="h-4 w-4" />
    </button>
  );
}

/**
 * Ordered recording modules with "+" slots at the beginning, between every pair, and at the end.
 */
export function PitchModuleStrip({
  segments = [],
  project,
  activeIdx = 0,
  disabled = false,
  onSelect,
  onInsert,
  onDelete,
}) {
  if (!segments.length) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-dashed border-slate-700 p-4 text-sm text-slate-400">
        <InsertButton position={0} label="Add the first module" onInsert={onInsert} disabled={disabled} />
        No modules yet — add one to start recording.
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="pitch-module-strip">
      <InsertButton position={0} label="Add a module at the beginning" onInsert={onInsert} disabled={disabled} />
      {segments.map((seg, i) => {
        const { capture, recordingMode } = resolveSegmentConfig(seg, project);
        const CaptureIcon = CAPTURE_ICONS[capture] || Mic;
        const ModeIcon = recordingMode === 'teleprompter' ? ScrollText : Waves;
        const active = i === activeIdx;
        const recorded = Boolean(seg.recording?.audio_url);
        const title = seg.title || `Part ${i + 1}`;
        const isLast = i === segments.length - 1;
        return (
          <Fragment key={seg.id}>
            <div
              className={`group flex items-center rounded-full border text-sm transition-colors ${
                active
                  ? 'border-amber-400 bg-amber-400/15 text-amber-100'
                  : 'border-slate-700 text-slate-300 hover:border-slate-500'
              }`}
            >
              <button
                type="button"
                onClick={() => onSelect(i)}
                disabled={disabled && !active}
                className="flex items-center gap-1.5 py-1.5 pl-3 pr-2 disabled:cursor-not-allowed disabled:opacity-60"
                title={`${title} · ${recordingMode === 'teleprompter' ? 'Teleprompter' : 'Independent audio'} · ${captureLabel(capture)}`}
              >
                {recorded ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : null}
                <span className="max-w-[10rem] truncate">{title}</span>
                <ModeIcon className="h-3 w-3 opacity-60" />
                <CaptureIcon className="h-3 w-3 opacity-60" />
              </button>
              <button
                type="button"
                onClick={() => onDelete(seg)}
                disabled={disabled}
                aria-label={`Delete ${title}`}
                title={`Delete ${title}`}
                data-testid={`pitch-delete-module-${seg.id}`}
                className="mr-1 flex h-6 w-6 items-center justify-center rounded-full text-slate-500 hover:bg-red-500/15 hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <InsertButton
              position={i + 1}
              label={isLast ? 'Add a module at the end' : `Add a module between "${title}" and "${segments[i + 1].title || `Part ${i + 2}`}"`}
              onInsert={onInsert}
              disabled={disabled}
            />
          </Fragment>
        );
      })}
    </div>
  );
}

export default PitchModuleStrip;
